/**
 * Phase 9.7.13 — 🎬 Home Hero / Spotlight Carousel
 *
 * Auto-advancing carousel mounted at the top of /home. Renders slides
 * authored by founder/admin via /admin/hero-slides.
 *
 * Slide types: spotlight_of_week, featured_push, top_beam_host,
 * top_supporter, interview_event, announcement.
 *
 * Hidden when there are no active slides.
 */
import { useEffect, useState, useRef } from "react";
import { optimizeImage } from "@/lib/mediaUrls";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const TYPE_BADGE = {
  spotlight_of_week: { label: "🌟 Spotlight of the Week", color: "from-purple-500 to-amber-400" },
  featured_push:     { label: "🚀 Featured Push",         color: "from-amber-500 to-pink-500" },
  top_beam_host:     { label: "🎙 Top Beam Host",         color: "from-cyan-400 to-purple-500" },
  top_supporter:     { label: "👑 Top Supporter",         color: "from-yellow-400 to-pink-500" },
  interview_event:   { label: "🎤 Interview / Event",     color: "from-pink-500 to-purple-600" },
  announcement:      { label: "📣 Announcement",          color: "from-emerald-400 to-cyan-500" },
};

// Phase 21.3a5 Batch 2 — presentation-mode helpers.
const LABEL_SIZE_CLASSES = {
  sm: "text-[10px] px-2 py-0.5",
  md: "text-xs px-3 py-1",
  lg: "text-sm px-4 py-1.5",
};
const TITLE_SIZE_CLASSES = {
  sm: "text-lg sm:text-xl",
  md: "text-2xl sm:text-3xl md:text-4xl",
  lg: "text-3xl sm:text-4xl md:text-5xl",
};

const ROTATE_MS = 6000;

const HomeHeroCarousel = () => {
  const [slides, setSlides] = useState([]);
  const [presentation, setPresentation] = useState({
    default_label_size: "md",
    default_title_size: "md",
    default_display_mode: "full",
  });
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const hoveredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      api.get("/hero-slides"),
      api.get("/homepage/config"),
    ]).then(([slidesRes, cfgRes]) => {
      if (cancelled) return;
      if (slidesRes.status === "fulfilled") {
        setSlides(slidesRes.value.data?.slides || []);
      }
      if (cfgRes.status === "fulfilled") {
        const ap = cfgRes.value.data?.announcement_presentation;
        if (ap) setPresentation((p) => ({ ...p, ...ap }));
      }
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    timerRef.current = setInterval(() => {
      if (!hoveredRef.current) setIdx((i) => (i + 1) % slides.length);
    }, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  if (!slides.length) return null;

  const slide = slides[idx];
  const badge = TYPE_BADGE[slide.type] || TYPE_BADGE.announcement;

  // Phase 21.3a5 Batch 2 — resolve per-slide overrides against defaults.
  const labelSize = slide.label_size || presentation.default_label_size || "md";
  const titleSize = slide.title_size || presentation.default_title_size || "md";
  const displayMode = slide.display_mode || presentation.default_display_mode || "full";
  const showLabel = displayMode !== "banner_only" && displayMode !== "title_only";
  const showTitle = !!slide.title && displayMode !== "banner_only" && displayMode !== "label_only";
  const showSubtitleAndCta = displayMode === "full";

  return (
    <div
      data-testid="home-hero-carousel"
      className="relative rounded-2xl overflow-hidden mb-6 group"
      onMouseEnter={() => { hoveredRef.current = true; }}
      onMouseLeave={() => { hoveredRef.current = false; }}
    >
      <div
        className="relative h-[220px] sm:h-[300px] md:h-[340px] transition-all duration-700"
        style={{
          backgroundImage: slide.image_url ? `url(${optimizeImage(slide.image_url, 'cover')})` : undefined,
          backgroundColor: "#1a0f2e",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid={`hero-slide-${slide.slide_id}`}
        data-display-mode={displayMode}
      >
        {/* gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

        {/* slide type badge */}
        {showLabel && (
          <div className="absolute top-4 left-4">
            <span
              className={`inline-flex items-center rounded-full font-black uppercase tracking-wider text-white bg-gradient-to-r ${badge.color} shadow-md ${LABEL_SIZE_CLASSES[labelSize] || LABEL_SIZE_CLASSES.md}`}
              data-testid={`hero-slide-label-${slide.slide_id}`}
            >
              {badge.label}
            </span>
          </div>
        )}

        {/* content */}
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
          {showTitle && (
            <h2 className={`${TITLE_SIZE_CLASSES[titleSize] || TITLE_SIZE_CLASSES.md} font-black text-white drop-shadow-lg max-w-3xl`} data-testid={`hero-slide-title-${slide.slide_id}`}>
              {slide.title}
            </h2>
          )}
          {showSubtitleAndCta && slide.subtitle && (
            <p className="mt-2 text-sm sm:text-base text-pink-100/90 max-w-2xl">
              {slide.subtitle}
            </p>
          )}
          {/* Phase 9.7.21 — Robust CTA resolution.
              If admin set explicit cta_url use it. Otherwise fall back
              to a default route based on slide.type so legacy slides
              for "Spotlight Sundays" / "Virtual Gifts" / "Submit Music"
              / "Magazine" always land somewhere useful. */}
          {showSubtitleAndCta && (() => {
            const TYPE_FALLBACK_URLS = {
              spotlight: "/spotlight",
              gifts: "/virtual-gifts",
              "virtual-gifts": "/virtual-gifts",
              submit: "/submit",
              magazine: "/magazine",
              feature: "/leaderboard/today",
              leaderboard: "/leaderboard/today",
            };
            const ctaUrl = slide.cta_url
              || TYPE_FALLBACK_URLS[slide.type]
              || null;
            const ctaLabel = slide.cta_label
              || (slide.type === "spotlight" ? "View Spotlight Sundays"
                : slide.type === "gifts" || slide.type === "virtual-gifts" ? "Explore Virtual Gifts"
                : slide.type === "submit" ? "Submit Music"
                : slide.type === "magazine" ? "Read Magazine"
                : slide.type === "feature" || slide.type === "leaderboard" ? "See the leaderboard"
                : null);
            if (!ctaUrl || !ctaLabel) return null;
            const isExternal = ctaUrl.startsWith("http");
            return (
              <Link
                to={ctaUrl}
                target={isExternal ? "_blank" : "_self"}
                className="inline-flex items-center mt-4 px-5 py-2 rounded-full bg-white text-black font-bold text-sm hover:bg-amber-200 transition-colors"
                data-testid={`hero-cta-${slide.slide_id}`}
              >
                {ctaLabel} →
              </Link>
            );
          })()}
        </div>
      </div>

      {/* dot indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-3 right-4 flex gap-1.5">
          {slides.map((_s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`w-2 h-2 rounded-full transition-all ${
                i === idx ? "bg-white w-6" : "bg-white/40 hover:bg-white/70"
              }`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HomeHeroCarousel;
